import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';

const { 
  FiDatabase, FiFileText, FiCalendar, FiCpu, 
  FiZap, FiAward, FiClock, FiTarget, FiBarChart2,
  FiCheckCircle, FiActivity, FiAlertTriangle, FiPlusCircle
} = FiIcons;

const Dashboard = () => {
  const navigate = useNavigate();
  const questions = storage.getQuestions();
  const papers = storage.getPapers();

  // Calculate stats
  const totalMarks = questions.reduce((sum, q) => sum + (parseInt(q.marks) || 0), 0);
  const avgQuestionsPerPaper = papers.length ? 
    Math.round(papers.reduce((sum, p) => sum + (p.questions?.length || 0), 0) / papers.length) : 0;
  
  // Get question types distribution
  const questionTypes = {};
  questions.forEach(q => {
    if (q.type) {
      questionTypes[q.type] = (questionTypes[q.type] || 0) + 1;
    }
  });
  
  // Top question type
  let topQuestionType = { type: 'None', count: 0 };
  Object.entries(questionTypes).forEach(([type, count]) => {
    if (count > topQuestionType.count) {
      topQuestionType = { type, count };
    }
  });

  const stats = [
    {
      title: 'Total Questions',
      value: questions.length,
      icon: FiDatabase,
      gradient: 'from-blue-500 to-cyan-500',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Papers Generated',
      value: papers.length,
      icon: FiFileText,
      gradient: 'from-emerald-500 to-teal-500',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Question Bank Value',
      value: `${totalMarks} marks`,
      icon: FiBarChart2,
      gradient: 'from-purple-500 to-pink-500',
      change: '+18%',
      changeType: 'positive'
    },
    {
      title: 'Avg Questions/Paper',
      value: avgQuestionsPerPaper,
      icon: FiActivity,
      gradient: 'from-orange-500 to-red-500',
      change: '+5%',
      changeType: 'positive'
    }
  ];

  const recentQuestions = questions.slice(-5).reverse();
  const recentPapers = papers.slice(-3).reverse();

  // Activity timeline data
  const activityTimeline = [
    { 
      title: 'Paper Generated', 
      time: '2 hours ago', 
      description: 'Final Exam Paper - Mathematics Grade 10',
      icon: FiFileText,
      color: 'bg-blue-100 text-blue-600'
    },
    { 
      title: 'Questions Added', 
      time: '5 hours ago', 
      description: 'Added 15 new questions to Physics question bank',
      icon: FiPlusCircle,
      color: 'bg-green-100 text-green-600'
    },
    { 
      title: 'Exam Scheduled', 
      time: '1 day ago', 
      description: 'Mid-term assessment scheduled for next week',
      icon: FiCalendar,
      color: 'bg-purple-100 text-purple-600'
    },
    { 
      title: 'Template Created', 
      time: '2 days ago', 
      description: 'New university paper template created',
      icon: FiCpu,
      color: 'bg-amber-100 text-amber-600'
    },
  ];

  // Handle Quick Generate button click
  const handleQuickGenClick = () => {
    navigate('/paper-generator');
  };

  return (
    <div className="space-y-8 p-8 ml-72 relative z-10">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-purple-100/30 rounded-full -mr-48 -mt-48 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-green-100/30 to-teal-100/30 rounded-full -ml-48 -mb-48 blur-3xl"></div>
      
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start relative z-10"
      >
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Dashboard Overview</h2>
          <p className="text-slate-600">Here's what's happening with your exam generator today.</p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleQuickGenClick}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2"
          >
            <SafeIcon icon={FiZap} />
            <span>Quick Generate</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5, scale: 1.02 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group relative z-20"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <SafeIcon icon={stat.icon} className="text-white text-xl" />
              </div>
              <div className={`text-xs font-bold px-2 py-1 rounded-full ${
                stat.changeType === 'positive' 
                  ? 'text-emerald-700 bg-emerald-100' 
                  : 'text-red-700 bg-red-100'
              }`}>
                {stat.change}
              </div>
            </div>
            <div>
              <h3 className="text-slate-600 text-sm font-medium">{stat.title}</h3>
              <p className="text-3xl font-bold text-slate-900 mt-1 group-hover:scale-105 transition-transform duration-300">{stat.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
        {/* Recent Questions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 relative z-20 lg:col-span-1"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <SafeIcon icon={FiTarget} className="text-emerald-600 text-lg" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Recent Questions</h3>
            </div>
            <button className="text-purple-600 hover:text-purple-700 text-sm font-semibold">View All</button>
          </div>
          <div className="space-y-4">
            {recentQuestions.length > 0 ? (
              recentQuestions.map((question, index) => (
                <motion.div
                  key={question.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start space-x-4 p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-100 hover:shadow-md transition-all duration-300"
                >
                  <div className={`p-2 rounded-lg ${
                    question.difficulty === 'Easy' 
                      ? 'bg-green-100 text-green-600'
                      : question.difficulty === 'Medium'
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    <SafeIcon icon={FiAward} className="text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {question.text}
                    </p>
                    <div className="flex items-center space-x-3 mt-2 text-xs text-slate-600">
                      <span className="bg-white px-2 py-1 rounded-full">{question.subject}</span>
                      <span>{question.type}</span>
                      <span className="font-semibold">{question.marks} marks</span>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiTarget} className="mx-auto text-4xl text-slate-300 mb-3" />
                <p className="text-slate-500">No questions added yet</p>
                <p className="text-sm text-slate-400 mt-1">Create your first question to get started</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Papers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 relative z-20 lg:col-span-1"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <SafeIcon icon={FiFileText} className="text-blue-600 text-lg" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Recent Papers</h3>
            </div>
            <button className="text-purple-600 hover:text-purple-700 text-sm font-semibold">View All</button>
          </div>
          <div className="space-y-4">
            {recentPapers.length > 0 ? (
              recentPapers.map((paper, index) => (
                <motion.div
                  key={paper.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 bg-gradient-to-r from-slate-50 to-purple-50 rounded-xl border border-slate-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <SafeIcon icon={FiFileText} className="text-purple-600 text-sm" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{paper.title}</p>
                        <div className="flex items-center space-x-3 mt-2 text-xs text-slate-600">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiTarget} className="text-xs" />
                            <span>{paper.questions?.length || 0} questions</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiAward} className="text-xs" />
                            <span>{paper.totalMarks} marks</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-slate-500">
                      <SafeIcon icon={FiClock} className="text-xs" />
                      <span>{new Date(paper.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiFileText} className="mx-auto text-4xl text-slate-300 mb-3" />
                <p className="text-slate-500">No papers generated yet</p>
                <p className="text-sm text-slate-400 mt-1">Generate your first paper to see it here</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Question Bank Analytics */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 relative z-20 lg:col-span-1"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <SafeIcon icon={FiBarChart2} className="text-indigo-600 text-lg" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Question Bank Analytics</h3>
          </div>
          
          <div className="space-y-6">
            {/* Most Common Question Type */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-xl border border-indigo-100">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Most Common Question Type</h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <SafeIcon icon={FiCheckCircle} className="text-indigo-600 text-sm" />
                  </div>
                  <span className="font-semibold text-slate-900">{topQuestionType.type}</span>
                </div>
                <span className="bg-white px-2 py-1 rounded-full text-xs font-medium text-indigo-700">
                  {topQuestionType.count} questions
                </span>
              </div>
            </div>
            
            {/* Question Distribution */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Question Difficulty</h4>
              <div className="space-y-3">
                {['Easy', 'Medium', 'Hard'].map(difficulty => {
                  const count = questions.filter(q => q.difficulty === difficulty).length;
                  const percentage = questions.length ? Math.round((count / questions.length) * 100) : 0;
                  
                  return (
                    <div key={difficulty} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-slate-700">{difficulty}</span>
                        <span className="text-slate-600">{percentage}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            difficulty === 'Easy' ? 'bg-green-500' : 
                            difficulty === 'Medium' ? 'bg-yellow-500' : 
                            'bg-red-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Question Coverage */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Subject Coverage</h4>
              <div className="flex flex-wrap gap-2 mt-2">
                {[...new Set(questions.map(q => q.subject))].slice(0, 5).map(subject => (
                  <span 
                    key={subject} 
                    className="px-3 py-1 bg-white rounded-full text-xs font-medium text-slate-700 shadow-sm"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Activity Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 relative z-10"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-amber-100 rounded-xl">
            <SafeIcon icon={FiActivity} className="text-amber-600 text-lg" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Recent Activity</h3>
        </div>
        
        <div className="relative pl-8 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
          {activityTimeline.map((activity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + (index * 0.1) }}
              className="mb-6 relative"
            >
              <div className={`absolute -left-8 p-1.5 rounded-full ${activity.color} border-4 border-white`}>
                <SafeIcon icon={activity.icon} className="text-xs" />
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="font-medium text-slate-900">{activity.title}</h4>
                  <span className="text-xs text-slate-500">{activity.time}</span>
                </div>
                <p className="text-sm text-slate-600">{activity.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;